import type { useQueryClient } from "@tanstack/react-query";
import type { ChatUser } from "@altek/ui/models/chat/chat-types";
import { invalidates, keys } from "@/lib/api/query-keys";

/**
 * What a chat WRITE invalidates — the other half of `ChatRealtimeBridge`, which answers frames.
 *
 * A write knows exactly what it changed and is allowed to be broad: the person is waiting for
 * their own action to show. A frame is somebody else's action arriving in the background, and
 * that is where breadth costs requests nobody asked for.
 */
/** The prefixes every chat write invalidates — `invalidates.chat`, applied. */
export function invalidateChat(queryClient: ReturnType<typeof useQueryClient>): Promise<unknown> {
  return Promise.all(
    invalidates.chat.map((key) => queryClient.invalidateQueries({ queryKey: key })),
  );
}

/**
 * The rail and the badge only — deliberately NOT the thread.
 *
 * Moving the read cursor changes two counters and no messages. Invalidating the thread as well
 * would refetch the whole open conversation every time a message is marked read, which is every
 * time one arrives: a page of history re-read for a number that is not even in it.
 */
export function invalidateChatCounters(
  queryClient: ReturnType<typeof useQueryClient>,
): Promise<unknown> {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: ["chat", "conversations"] }),
    queryClient.invalidateQueries({ queryKey: keys.chat.unreadCount() }),
  ]);
}

/**
 * AFTER LEAVING A CONVERSATION — the rail and the badge, NOT the conversation itself (20 Ekim 2026,
 * two-account round: the left group's detail and messages were refetched while its thread was
 * still on screen for a moment, and answered 404 three times). From now on that conversation does
 * not exist for the reader; the screen navigates away, and its cached entries go unused.
 */
export function invalidateAfterLeaving(queryClient: ReturnType<typeof useQueryClient>): Promise<unknown> {
  return Promise.all([
    queryClient.invalidateQueries({
      predicate: (query) =>
        query.queryKey[0] === "chat" && query.queryKey[1] === "conversations" && query.queryKey[2] !== "detail",
    }),
    queryClient.invalidateQueries({ queryKey: keys.chat.unreadCount() }),
  ]);
}

/**
 * THE BELL, after a read settled its chat rows (H4, 20 Ekim 2026): the count and the open list —
 * never `keys.notifications.all()`, which would refetch the announcements as well. Called only when
 * the server said a row changed (`notificationsCleared > 0`) or another tab of the reader read.
 */
export function invalidateBell(queryClient: ReturnType<typeof useQueryClient>): Promise<unknown> {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: keys.notifications.unreadCount() }),
    queryClient.invalidateQueries({ queryKey: ["notifications", "list"] }),
  ]);
}

/** Kit users for a set of ids, in the order the ids arrived. Unknown ids are dropped, not faked. */
export function typingUsersFrom(
  ids: ReadonlyArray<string>,
  members: Map<string, ChatUser>,
): ChatUser[] {
  return ids
    .map((id) => members.get(id))
    .filter((candidate): candidate is ChatUser => candidate !== undefined);
}
